import { memo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import Badge from 'components/field/Badge';
import Button from 'components/field/Button';
import Authorization from 'components/global/Authorization';
import Collapse from 'components/global/Collapse';
import Icon from 'components/global/Icon';
import LordIcon from 'components/global/LordIcon';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import useMediaQuery from 'hooks/useMediaQuery';

import { toogleSideBar } from 'stores/layoutSlice';

import { THEME } from 'utils/Theme';
import { Breakpoints } from 'utils/const.styles';

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { faCircleArrowUp } from '@fortawesome/pro-solid-svg-icons';

import * as S from './SideBar.styled';

const SideBarLink = ({ item, closedCategories, setClosedCategories }) => {
	const sideBarOpen = useSelector((state) => state.layout.sideBarOpen);
	const buttonRef = useRef(null);

	const toggleHeader = (section) => {
		const sections = [...closedCategories];
		if (sections.includes(section)) {
			setClosedCategories(sections.filter((item) => item !== section));
		} else {
			setClosedCategories([...sections, section]);
		}
	};

	return (
		<S.LinkComponent>
			{item.header && sideBarOpen && (
				<Button
					id={`${item.header}_category_button`}
					className={`${item.header}_category_button`}
					ref={buttonRef}
					variant="borderless"
					size="small"
					fullWidth
					onClick={() => toggleHeader(item.header)}
					css={S.HeaderButtonCss}>
					<S.SidebarTitle>
						<Typography fontSize="1.7rem" fontWeight={700}>
							{item.header}
						</Typography>
						{item.badge && <Badge variant="pill" size="small" color="red" text={item.badge} />}
					</S.SidebarTitle>
					<Icon icon={faChevronDown} rotation={closedCategories.includes(item.header) ? 0 : 180} />
				</Button>
			)}
			{sideBarOpen ? (
				<Collapse buttonRef={buttonRef} defaultVisible={!closedCategories.includes(item.header)}>
					{item.links.map((link, linkIndex) => (
						<Link key={linkIndex} link={link} />
					))}
				</Collapse>
			) : (
				!closedCategories.includes(item.header) &&
				item.links.map((link, linkIndex) => <Link key={linkIndex} link={link} />)
			)}
		</S.LinkComponent>
	);
};

const Link = ({ link }) => {
	const dispatch = useDispatch();
	const isMobile = useMediaQuery(Breakpoints.mobile);
	const darkMode = useSelector((state) => state.layout.darkMode);
	const sideBarOpen = useSelector((state) => state.layout.sideBarOpen);
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const firstPath = pathname.split('/')[1];
	const targetRef = useRef(null);

	return (
		<Authorization path={link?.external ? link?.role : link.path}>
			<Tooltip title={!sideBarOpen && link.title}>
				<Button
					ref={targetRef}
					id={`${link.title}_menu_button`}
					className={`${link.title}_menu_button`}
					component="a"
					href={link.path}
					variant={firstPath === link.path.split('/')[1] ? 'black' : 'borderless'}
					fullWidth
					onClick={(e) => {
						e.preventDefault();
						if (link?.external) {
							window.open(link.path, '_blank');
						} else {
							navigate(link?.path);
						}
						isMobile && dispatch(toogleSideBar());
					}}
					css={S.LinkButtonCss({
						isopen: sideBarOpen
					})}>
					<div className="content">
						{link.lordIcon ? (
							<LordIcon
								color={firstPath === link.path.split('/')[1] ? THEME.WHITE : ''}
								primaryColor={darkMode ? THEME.WHITE : THEME.BLACK}
								secondaryColor={darkMode ? THEME.DARK.PRIMARY_COLOR : THEME.LIGHT.PRIMARY_COLOR}
								width={'3.5rem'}
								height={'3.5rem'}
								src={link.lordIcon}
								target={targetRef}
							/>
						) : (
							<div>
								<Icon icon={link.icon} size={!sideBarOpen ? 'medium' : 'small'} />
							</div>
						)}

						{sideBarOpen && (
							<>
								<Typography fontSize="1.6rem" css={S.SideBarTextColor}>
									{link.title}
								</Typography>
								{link.badge && <Badge variant="pill" size="small" color="red" text={link.badge} />}
							</>
						)}
					</div>
					{sideBarOpen && link.upgrade && <Icon className="arrow" icon={faCircleArrowUp} />}
				</Button>
			</Tooltip>
		</Authorization>
	);
};

export default memo(SideBarLink);
